import {
  Bell,
  Building2,
  ChevronRight,
  KeyRound,
  LogOut,
  Shield,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './surfacePrimitives.js';
import { DetailDrawer } from './DetailDrawer.js';

export interface AdminProfileSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Authenticated: Sign out / Odhlásit se. */
  readonly onLogout?: () => void;
  /** Guest surfaces: Sign in (e.g. navigate to login). */
  readonly onSignIn?: () => void;
  /**
   * Spec §11.2 Můj účet — when set, shows the Account Hub row.
   * Privacy / login-security live inside Account Hub until dedicated
   * surfaces exist — do not pass the same handler three times.
   */
  readonly onOpenAccount?: () => void;
  /**
   * Spec §11.2 Soukromí — only when a dedicated privacy surface exists
   * (different destination from Account Hub). Omit when it would duplicate
   * onOpenAccount.
   */
  readonly onOpenPrivacy?: () => void;
  /**
   * Spec §11.2 Přihlášení a zabezpečení — only when a dedicated credentials /
   * MFA / OIDC surface exists. Omit when it would duplicate onOpenAccount.
   */
  readonly onOpenLoginSecurity?: () => void;
  /**
   * Spec §11.2 Oznámení — only when notifications prefs exist (“if exist”).
   */
  readonly onOpenNotifications?: () => void;
  readonly logoutTestId?: string;
  readonly logoutLabelKey?: string;
  readonly signInTestId?: string;
  /** Busy-dismiss gate (DetailDrawer isBusy) — Escape/backdrop blocked when true. */
  readonly isBusy?: boolean;
}

const PROFILE_MENU_ROW_CLASS = [
  'flex w-full items-start gap-3 rounded-md px-2 py-2.5 text-left',
  'text-[var(--color-an-text)]',
  'hover:bg-[var(--color-an-surface-hover)]',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-an-focus-ring)]',
].join(' ');

/**
 * Profile overlay — Spec §11.2 IA:
 * Můj účet · [Soukromí / Přihlášení only if dedicated] · Oznámení (if exist) · Odhlásit se
 * Hosted on DetailDrawer variant=sheet (LOCK A) + DialogCloseButton Lucide X 20/44.
 */
export function AdminProfileSheet({
  open,
  onClose,
  onLogout,
  onSignIn,
  onOpenAccount,
  onOpenPrivacy,
  onOpenLoginSecurity,
  onOpenNotifications,
  logoutTestId = 'admin-logout-btn',
  logoutLabelKey = 'chrome.settings.signOut',
  signInTestId = 'admin-sign-in-btn',
  isBusy = false,
}: AdminProfileSheetProps): JSX.Element {
  const { t } = useTranslation('admin');

  /** Account Hub present → single Můj účet row (covers privacy + security until split). */
  const showAccountHub = onOpenAccount !== undefined;
  /** Dedicated destinations only — never fall back to onOpenAccount (duplicate rows). */
  const showPrivacy = onOpenPrivacy !== undefined;
  const showLoginSecurity = onOpenLoginSecurity !== undefined;
  /** Spec §11.2 — Oznámení only when a notifications handler exists. */
  const showNotifications = onOpenNotifications !== undefined;
  const showSignOut = onLogout !== undefined;
  const showGuestSignIn = onSignIn !== undefined;
  const hasMenuBody = showAccountHub || showSignOut;

  const runAndClose = (action: () => void): void => {
    onClose();
    action();
  };

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={t('chrome.profile.title')}
      variant="sheet"
      placement="topEnd"
      closeGlyph="x"
      isBusy={isBusy}
      contentTestId="admin-profile-sheet"
      overlayTestId="admin-profile-sheet-backdrop"
      closeTestId="admin-profile-sheet-close"
      exportExclude="chrome"
    >
      {hasMenuBody ? (
        <div
          className="flex flex-col"
          role="menu"
          aria-label={t('chrome.profile.title')}
        >
          {showAccountHub ? (
            <>
              <button
                type="button"
                role="menuitem"
                className={PROFILE_MENU_ROW_CLASS}
                data-testid="admin-profile-account"
                onClick={() => {
                  runAndClose(onOpenAccount);
                }}
              >
                <Building2
                  className="mt-0.5 h-5 w-5 shrink-0 stroke-[1.75] text-[var(--color-an-text)]"
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold leading-snug">
                    {t('chrome.profile.myAccount')}
                  </span>
                  <span className="mt-0.5 block text-xs leading-snug text-[var(--color-an-text-muted)]">
                    {t('chrome.profile.accountDescription')}
                  </span>
                </span>
                <ChevronRight
                  className="mt-1 h-5 w-5 shrink-0 text-[var(--color-an-text-muted)]"
                  aria-hidden
                />
              </button>

              {showPrivacy ? (
                <button
                  type="button"
                  role="menuitem"
                  className={PROFILE_MENU_ROW_CLASS}
                  data-testid="admin-profile-privacy"
                  onClick={() => {
                    runAndClose(onOpenPrivacy);
                  }}
                >
                  <Shield
                    className="mt-0.5 h-5 w-5 shrink-0 stroke-[1.75] text-[var(--color-an-text)]"
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold leading-snug">
                      {t('chrome.profile.privacy')}
                    </span>
                    <span className="mt-0.5 block text-xs leading-snug text-[var(--color-an-text-muted)]">
                      {t('chrome.profile.privacyDescription')}
                    </span>
                  </span>
                  <ChevronRight
                    className="mt-1 h-5 w-5 shrink-0 text-[var(--color-an-text-muted)]"
                    aria-hidden
                  />
                </button>
              ) : null}

              {showLoginSecurity ? (
                <button
                  type="button"
                  role="menuitem"
                  className={PROFILE_MENU_ROW_CLASS}
                  data-testid="admin-profile-login-security"
                  onClick={() => {
                    runAndClose(onOpenLoginSecurity);
                  }}
                >
                  <KeyRound
                    className="mt-0.5 h-5 w-5 shrink-0 stroke-[1.75] text-[var(--color-an-text)]"
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold leading-snug">
                      {t('chrome.profile.loginSecurity')}
                    </span>
                    <span className="mt-0.5 block text-xs leading-snug text-[var(--color-an-text-muted)]">
                      {t('chrome.profile.loginSecurityDescription')}
                    </span>
                  </span>
                  <ChevronRight
                    className="mt-1 h-5 w-5 shrink-0 text-[var(--color-an-text-muted)]"
                    aria-hidden
                  />
                </button>
              ) : null}

              {showNotifications ? (
                <button
                  type="button"
                  role="menuitem"
                  className={PROFILE_MENU_ROW_CLASS}
                  data-testid="admin-profile-notifications"
                  onClick={() => {
                    runAndClose(onOpenNotifications);
                  }}
                >
                  <Bell
                    className="mt-0.5 h-5 w-5 shrink-0 stroke-[1.75] text-[var(--color-an-text)]"
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold leading-snug">
                      {t('chrome.profile.notifications')}
                    </span>
                    <span className="mt-0.5 block text-xs leading-snug text-[var(--color-an-text-muted)]">
                      {t('chrome.profile.notificationsDescription')}
                    </span>
                  </span>
                  <ChevronRight
                    className="mt-1 h-5 w-5 shrink-0 text-[var(--color-an-text-muted)]"
                    aria-hidden
                  />
                </button>
              ) : null}
            </>
          ) : null}

          {showAccountHub && showSignOut ? (
            <div
              className="my-1 border-t border-[var(--color-an-border)]"
              role="separator"
              aria-hidden="true"
            />
          ) : null}

          {showSignOut ? (
            <button
              type="button"
              role="menuitem"
              className={PROFILE_MENU_ROW_CLASS}
              data-testid={logoutTestId}
              onClick={() => {
                runAndClose(() => {
                  onLogout?.();
                });
              }}
            >
              <LogOut
                className="mt-0.5 h-5 w-5 shrink-0 stroke-[1.75] text-[var(--color-an-text)]"
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold leading-snug">{t(logoutLabelKey)}</span>
                <span className="mt-0.5 block text-xs leading-snug text-[var(--color-an-text-muted)]">
                  {t('chrome.profile.signOutDescription')}
                </span>
              </span>
            </button>
          ) : null}
        </div>
      ) : null}

      {showGuestSignIn ? (
        <div className={[hasMenuBody ? 'mt-3' : '', 'flex flex-col gap-2'].filter(Boolean).join(' ')}>
          <Button
            type="button"
            intent="primary"
            size="sm"
            className="w-full"
            data-testid={signInTestId}
            onClick={() => {
              onClose();
              onSignIn?.();
            }}
          >
            {t('chrome.profile.signIn')}
          </Button>
        </div>
      ) : null}
    </DetailDrawer>
  );
}
